import React from 'react';
import { Tooltip } from '@mui/material';
import { FaTrashCan, FaPenToSquare, FaTrashCanArrowUp, FaArrowUp, FaFileLines } from "react-icons/fa6";
import { FaExternalLinkAlt } from "react-icons/fa";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config/config';
import { useAuth } from '../pages/login/AuthContext';

/**
 * AG Grid Cell Renderer for table actions with icons
 */
const IconActionsRenderer = (props) => {
  const { data, context } = props;
  const navigate = useNavigate();
  const { token } = useAuth();

  if (!data) return null;

  // Context'ten işlev ve yapılandırmaları çıkart
  const editHandler = context.handleEditItem || context.handleEditOrder;
  const deleteHandler = context.handleOpenDeleteModal;
  const forwardHandler = context.handleOpenForwardModal;
  const viewHandler = context.onView;
  const restoreHandler = context.onRestore;
  const copyHandler = context.onCopy;
  const showTeklifPreview = context.showTeklifPreview !== false; // Varsayılan olarak true

  const {
    userData,
    hasPermission,
    hasForwardOption,
    showRestore = false,
    showView = false,
    showEdit = true,
    showDelete = true,
    isOrderTable = false,
    isLicenseTable = false,
  } = context;

  // Duruma göre butonları gösterme/gizleme
  let orderShowDelete;
  let orderShowForward;
  let showCopy = false;
  let showOrderCopy = false;
  let showEditForOffer = true;

  // Get status from different possible fields
  const currentStatus = data.DurumAd || data.Durum?.DurumAdi || data.DurumAdi || "";

  if (isLicenseTable) {
    // Lisans tablosu: Sil / Aktife al sadece Bayi 1 (context.showDelete / showRestore ile gelir)
    orderShowDelete = showDelete;
    orderShowForward = hasForwardOption && showDelete &&
      (data.DurumAd !== "Onaylandı" && data.DurumAd !== "Lisans Üretildi") &&
      (data.YonlendirilenBayiId === userData?.BayiKodu || data.YonlendirilenBayiAdi === null);
    showCopy = false;
    showOrderCopy = false;
  } else if (data.DurumAd !== undefined) {
    orderShowDelete = showDelete && (data.DurumAd !== "Onaylandı" && data.DurumAd !== "Lisans Üretildi");
    orderShowForward = hasForwardOption && showDelete &&
      (data.DurumAd !== "Onaylandı" && data.DurumAd !== "Lisans Üretildi") &&
      (data.YonlendirilenBayiId === userData?.BayiKodu || data.YonlendirilenBayiAdi === null);

    showCopy = data.DurumAd === "Lisans Üretildi";
    showOrderCopy = (data.DurumAd === "Onaylandı");
  } else {
    orderShowDelete = showDelete;
    orderShowForward = false;
  }

  // For offer table, disable edit when status is "Sonuçlandı"
  if (currentStatus === "Sonuçlandı") {
    showEditForOffer = false;
  }

  // For offer table: Bayi 1 her teklifi silebilir; diğer bayiler sadece "Onayda Bekliyor" ve "Red Edildi" silebilir, "Onaylandı" (siparişe dönüşen) silemez
  const isBayiFullAccess = Number(userData?.BayiId ?? userData?.BayiKodu ?? 0) === 1;
  let showDeleteForOffer = true;
  if (currentStatus === "Sonuçlandı") {
    showDeleteForOffer = false;
  }
  if (!isBayiFullAccess && (currentStatus === "Onaylandı" || currentStatus === "Onaylanan sipariş")) {
    showDeleteForOffer = false;
  }

  // Fallback kopyalama yöntemi
  const copyToClipboard = (text) => {
    try {
      // Modern yöntem
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text)
          .then(() => true)
          .catch(() => false);
      }

      // Fallback yöntemi
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand('copy');
        textArea.remove();
        return Promise.resolve(true);
      } catch (err) {
        textArea.remove();
        return Promise.resolve(false);
      }
    } catch (err) {
      return Promise.resolve(false);
    }
  };

  // Lisans bilgilerini kopyalama işlevi
  const handleCopyLicenseInfo = () => {
    // Özel handler kontrolü
    if (copyHandler && typeof copyHandler === 'function') {
      copyHandler(data.Id);
      return;
    }

    try {
      // Verileri verilen formata göre hazırla
      const customer = data.Musteri || data;
      const license = data.Lisans || data;

      const licenseInfo = `
Müşteri: ${customer.Unvan || 'Belirtilmemiş'}
Lisans Numarası: ${customer.MusteriNo || data.LisansNo || 'Belirtilmemiş'}
Lisans Tipi: ${license.LisansTipi || data.LisansTipi || 'Belirtilmemiş'}
Client ID: ${license.UserNo || data.UserNo || 'Belirtilmemiş'}
Kullanıcı Sayısı: ${license.UserCount || data.KullaniciSayisi || "Belirtilmemiş"}
Başlangıç Tarihi: ${license.BaslangicTarih ? new Date(license.BaslangicTarih).toLocaleDateString("tr-TR") : data.BaslangicTarih ? new Date(data.BaslangicTarih).toLocaleDateString("tr-TR") : 'Belirtilmemiş'}
Bitiş Tarihi: ${license.BitisTarih ? new Date(license.BitisTarih).toLocaleDateString("tr-TR") : data.BitisTarih ? new Date(data.BitisTarih).toLocaleDateString("tr-TR") : 'Belirtilmemiş'}
      `.trim();

      // Kopyalama işlemini yap
      copyToClipboard(licenseInfo)
        .then((success) => {
          if (success) {
            toast.success("Lisans bilgileri kopyalandı!");
          } else {
            toast.error("Lisans bilgileri kopyalanamadı.");
          }
        });

    } catch (error) {
      console.error("Lisans bilgisi hazırlama hatası:", error);
      toast.error("Lisans bilgisi hazırlanırken hata oluştu");
    }
  };

  // Sipariş bilgilerini kopyalama işlevi
  const handleCopyOrderInfo = () => {
    // Özel handler kontrolü
    if (copyHandler && typeof copyHandler === 'function') {
      copyHandler(data.Id);
      return;
    }

    try {
      const orderInfo = `
Müşteri: ${data.Unvan || 'Belirtilmemiş'}
Lisans Numarası: ${data.MusteriNo || 'Belirtilmemiş'}
Lisans Tipi: ${data.LisansTipi || 'Belirtilmemiş'}
Client ID: ${data.UserNo || 'Belirtilmemiş'}
Kullanıcı Sayısı: ${data.KullaniciSayisi || "Belirtilmemiş"}
Başlangıç Tarihi: ${data.LisansBaslangicTarihi ? new Date(data.LisansBaslangicTarihi).toLocaleDateString("tr-TR") : 'Belirtilmemiş'}
Bitiş Tarihi: ${data.LisansBitisTarihi ? new Date(data.LisansBitisTarihi).toLocaleDateString("tr-TR") : 'Belirtilmemiş'}
      `.trim();

      // Kopyalama işlemini yap
      copyToClipboard(orderInfo)
        .then((success) => {
          if (success) {
            toast.success("Sipariş bilgileri kopyalandı!");
          } else {
            toast.error("Sipariş bilgileri kopyalanamadı.");
          }
        });

    } catch (error) {
      console.error("Sipariş bilgisi hazırlama hatası:", error);
      toast.error("Sipariş bilgisi hazırlanırken hata oluştu");
    }
  };

  return (
    <div className="btn-container">
      {showView && viewHandler && (
        <Tooltip title="Detaylar">
          <button
            className="btn-details"
            onClick={() => viewHandler(data.Id)}
          >
            <FaExternalLinkAlt />
          </button>
        </Tooltip>
      )}

      {showEdit && showEditForOffer && editHandler && (
        <Tooltip title="Düzenle">
          <button
            className="btn-edit"
            onClick={() => editHandler(data.Id)}
          >
            <FaPenToSquare />
          </button>
        </Tooltip>
      )}

      {orderShowDelete && (showDeleteForOffer || isLicenseTable) && deleteHandler && (!showRestore || isLicenseTable) && (
        <Tooltip title="Sil">
          <button
            className="btn-delete"
            onClick={() => deleteHandler(data.Id)}
          >
            <FaTrashCan />
          </button>
        </Tooltip>
      )}

      {showRestore && restoreHandler && (
        <Tooltip title="Aktife al">
          <button
            className="btn-edit"
            onClick={() => restoreHandler(data.Id)}
          >
            <FaTrashCanArrowUp />
          </button>
        </Tooltip>
      )}

      {orderShowForward && forwardHandler && !hasPermission('admin') && (
        <Tooltip title="Üst Bayiye Yönlendir">
          <button
            className="btn-forward"
            onClick={() => forwardHandler(data.Id)}
          >
            <FaArrowUp />
          </button>
        </Tooltip>
      )}

      {showCopy && (
        <Tooltip title="Lisans Bilgilerini Kopyala">
          <button
            className="btn-copy"
            onClick={handleCopyLicenseInfo}
          >
            <ContentCopyIcon fontSize="small" />
          </button>
        </Tooltip>
      )}

      {showOrderCopy && (
        <Tooltip title="Sipariş Bilgilerini Kopyala">
          <button
            className="btn-copy-secondary"
            onClick={handleCopyOrderInfo}
          >
            <ContentCopyIcon fontSize="small" />
          </button>
        </Tooltip>
      )}

    </div>
  );
};

export default IconActionsRenderer;